import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadCloud, FileText, Loader2, Sparkles, Trash2, Eye } from "lucide-react";

function AnalysisView({ text }) {
  // lightweight markdown rendering
  const lines = (text || "").split("\n");
  return (
    <div className="space-y-1.5 text-sm leading-relaxed">
      {lines.map((ln, i) => {
        if (ln.startsWith("### ")) return <h4 key={i} className="font-display font-semibold text-base mt-3">{ln.slice(4)}</h4>;
        if (ln.startsWith("## ")) return <h3 key={i} className="font-display font-semibold text-lg mt-3">{ln.slice(3)}</h3>;
        if (ln.startsWith("# ")) return <h3 key={i} className="font-display font-bold text-lg mt-3">{ln.slice(2)}</h3>;
        if (/^[-*] /.test(ln)) return <li key={i} className="ml-5 list-disc text-[#4B5950]">{ln.slice(2)}</li>;
        if (ln.trim() === "") return <div key={i} className="h-1" />;
        return <p key={i} className="text-[#4B5950]">{ln}</p>;
      })}
    </div>
  );
}

export default function Documents() {
  const { t } = useLang();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/documents").then((r) => { setDocs(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const analyze = async (id) => {
    setAnalyzing(id);
    try {
      const { data } = await api.post(`/documents/${id}/analyze`);
      setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, analysis: data.analysis, analysis_status: "done" } : d)));
      toast.success("Analysis complete.");
      setViewing({ ...docs.find((d) => d.id === id), analysis: data.analysis });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAnalyzing(null);
    }
  };

  const remove = async (id) => {
    await api.delete(`/documents/${id}`);
    setDocs((prev) => prev.filter((d) => d.id !== id));
    toast.success("Document removed.");
  };

  const view = async (d) => {
    if (d.analysis) { setViewing(d); return; }
    const { data } = await api.get(`/documents/${d.id}`);
    setViewing(data);
  };

  return (
    <div data-testid="documents-page" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><FileText className="h-7 w-7 text-[#2E7599]" /> {t("doc.title")}</h1>
        <p className="text-[#4B5950] mt-1">{t("doc.subtitle")}</p>
      </div>

      <div
        data-testid="document-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
        className={`cursor-pointer border-2 border-dashed rounded-sm py-12 text-center transition-all duration-200 ${drag ? "border-[#275C3F] bg-[#EAF0EA]" : "border-[#DCE3DD] bg-white hover:border-[#275C3F]/50"}`}
      >
        <input ref={inputRef} data-testid="document-file-input" type="file" className="hidden" accept=".pdf,.txt,.csv,.md,.log" onChange={(e) => upload(e.target.files?.[0])} />
        {uploading ? (
          <div className="flex flex-col items-center gap-2"><Loader2 className="h-7 w-7 animate-spin text-[#275C3F]" /><p className="text-sm text-[#4B5950]">{t("doc.uploading")}</p></div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <UploadCloud className="h-9 w-9 text-[#275C3F]" />
            <p className="font-medium text-[#121A14]">{t("doc.dropHint")}</p>
            <p className="text-xs text-[#4B5950]">{t("doc.dropTypes")}</p>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#275C3F]" /></div>
      ) : docs.length === 0 ? (
        <p className="text-center text-[#4B5950] py-6">{t("doc.noDocs")}</p>
      ) : (
        <div className="grid gap-3">
          {docs.map((d) => (
            <div key={d.id} data-testid={`document-row-${d.id}`} className="bg-white border border-[#DCE3DD] rounded-sm p-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 grid place-items-center bg-[#EAF0EA] rounded-sm shrink-0"><FileText className="h-5 w-5 text-[#2E7599]" /></div>
                <div className="min-w-0">
                  <p className="font-medium text-[#121A14] truncate">{d.original_filename}</p>
                  <p className="text-xs text-[#4B5950]">
                    {d.size < 1024 ? `${d.size} B` : `${(d.size / 1024).toFixed(0)} KB`} · {new Date(d.created_at).toLocaleDateString()}
                    {d.analysis_status === "done" && <span className="text-[#6D9C55] ml-2">✓ {t("doc.analyzed")}</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {d.analysis_status === "done" ? (
                  <button data-testid={`view-analysis-${d.id}`} onClick={() => view(d)} className="inline-flex items-center gap-1.5 text-sm text-[#275C3F] border border-[#DCE3DD] px-3 py-1.5 rounded-sm hover:bg-[#EAF0EA]">
                    <Eye className="h-4 w-4" /> {t("doc.view")}
                  </button>
                ) : (
                  <button data-testid={`analyze-document-${d.id}`} onClick={() => analyze(d.id)} disabled={analyzing === d.id} className="inline-flex items-center gap-1.5 text-sm bg-[#275C3F] hover:bg-[#1E4731] text-white px-3 py-1.5 rounded-sm disabled:opacity-60">
                    {analyzing === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {analyzing === d.id ? t("doc.analyzing") : t("doc.analyze")}
                  </button>
                )}
                <button data-testid={`delete-document-${d.id}`} onClick={() => remove(d.id)} className="text-[#B84A22] hover:bg-[#B84A22]/10 p-1.5 rounded-sm"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-2xl">
          {viewing && (
            <>
              <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#275C3F]" /> {t("doc.aiAnalysis")} — {viewing.original_filename}</DialogTitle>
                <DialogDescription>{t("doc.analysisDesc")}</DialogDescription>
              </DialogHeader>
              <div className="max-h-[70vh] overflow-y-auto es-scroll pr-1">
                {viewing.analysis ? <AnalysisView text={viewing.analysis} /> : <p className="text-sm text-[#4B5950]">No analysis yet.</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
